import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AppState } from '.';
import { CouponInfo, SerializedException, exceptionOf } from '../models';
import * as CouponService from '../services/coupon';

export const COUPON_FEATURE_KEY = 'coupon';

export interface CouponState {
  coupon: CouponInfo | null;
  loading: boolean;
  errors: SerializedException[];
}

export const createInitialState = (): CouponState => ({
  coupon: null,
  loading: false,
  errors: [],
});

export const doFetchCouponByCode = createAsyncThunk(
  'coupon/fetchCouponByCode',
  async (code: string, { rejectWithValue }) => {
    try {
      return await CouponService.getCouponByCode(code);
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const couponSlice = createSlice({
  name: COUPON_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {
    clearCoupon: state => {
      state.coupon = null;
    },
    clearErrors: state => {
      state.errors = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(doFetchCouponByCode.pending, state => {
        state.loading = true;
        state.errors = [];
      })
      .addCase(
        doFetchCouponByCode.fulfilled,
        (state, action: PayloadAction<CouponInfo | null>) => {
          state.loading = false;
          state.coupon = action.payload;
        },
      )
      .addCase(doFetchCouponByCode.rejected, (state, action) => {
        state.loading = false;
        state.coupon = null;
        state.errors = [action.payload as SerializedException];
      });
  },
});

// Selectors
export const selectCouponState = (state: AppState) => state[COUPON_FEATURE_KEY];

export const selectCoupon = (state: AppState) => selectCouponState(state).coupon;

export const selectCouponLoading = (state: AppState) => selectCouponState(state).loading;

export const selectCouponErrors = (state: AppState) => selectCouponState(state).errors;

// Actions
export const { clearCoupon, clearErrors } = couponSlice.actions;

// Reducer
export default couponSlice.reducer;

export const fromCoupon = {
  doFetchCouponByCode,
  selectCoupon,
  selectCouponLoading,
  selectCouponErrors,
  clearCoupon,
  clearErrors,
};
